import type { PortfolioRow } from "../components/portfolio/columns/columnTypes";
import type { FxRates } from "../services/tradeCalculationService";
import type { IbkrPositionV2 } from "../types";
import type { OptionPriceResult } from "../types/marketData";

export function isFinitePositiveNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value) && value > 0;
}

export function isUsableFxRate(value: unknown): boolean {
  return isFinitePositiveNumber(value);
}

export function getFxRate(currency: string, fxRates: FxRates): number | null {
  const normalized = currency.toUpperCase();
  if (normalized === "USD") return 1.0;
  const rate = fxRates[normalized];
  if (!isUsableFxRate(rate)) {
    return null;
  }
  return rate;
}

export function isOptionPosition(secType: string | null | undefined): boolean {
  return (secType ?? "").toUpperCase() === "OPT";
}

export function selectConservativeOptionPrice(
  quote: OptionPriceResult,
  shares: number
): number | null {
  const usable = (n: number | null | undefined): number | null =>
    typeof n === "number" && Number.isFinite(n) && n > 0 ? n : null;
  const midRaw =
    Number.isFinite(quote.bid) && Number.isFinite(quote.ask) ? (quote.bid + quote.ask) / 2 : null;
  const side = shares < 0 ? usable(quote.ask) : usable(quote.bid);
  return side ?? usable(quote.close) ?? usable(midRaw);
}

export function resolveOptionPriceAndValue(
  pos: IbkrPositionV2,
  shares: number,
  optionInfo?: { quote?: OptionPriceResult; multiplier?: number },
  fallbackCurrency = ""
): { price: number; mktValue: number; currency: string } {
  const multiplier = optionInfo?.multiplier;
  const usableMultiplier =
    typeof multiplier === "number" && Number.isFinite(multiplier) && multiplier > 0;
  const livePrice = optionInfo?.quote
    ? selectConservativeOptionPrice(optionInfo.quote, shares)
    : null;

  if (livePrice !== null && usableMultiplier) {
    return {
      price: livePrice,
      mktValue: shares * livePrice * (multiplier as number),
      currency: pos.currency || fallbackCurrency,
    };
  }

  return {
    price: Number(pos.mktPrice) || 0,
    mktValue: Number(pos.mktValue) || 0,
    currency: pos.currency || fallbackCurrency,
  };
}

export function positionToRow(
  pos: IbkrPositionV2,
  priceOverride?: { price: number; currency: string },
  netLiq?: number,
  timeZoneMap?: Record<number, string>,
  fxRates?: FxRates,
  minSize?: number,
  optionInfo?: { quote?: OptionPriceResult; multiplier?: number },
  fallbackCurrency = ""
): PortfolioRow {
  const shares = Number(pos.position) || 0;

  let price: number;
  let mktValue: number;
  let currency: string;
  if (isOptionPosition(pos.secType)) {
    const resolved = resolveOptionPriceAndValue(pos, shares, optionInfo, fallbackCurrency);
    price = resolved.price;
    mktValue = resolved.mktValue;
    currency = resolved.currency;
  } else {
    const usableOverride = priceOverride && isFinitePositiveNumber(priceOverride.price)
      ? priceOverride
      : undefined;
    price = usableOverride?.price ?? (Number(pos.mktPrice) || 0);
    mktValue = shares * price;
    currency = usableOverride?.currency || pos.currency || fallbackCurrency;
  }

  const unrealizedPnl = Number(pos.unrealizedPnl) || 0;
  const fxRate = getFxRate(currency, fxRates ?? {});
  const priceUsd = fxRate !== null ? price * fxRate : null;
  const mktValueUsd = fxRate !== null ? mktValue * fxRate : null;
  const unrealizedPnlUsd = fxRate !== null ? unrealizedPnl * fxRate : null;

  let currentWeightPct: number | null = null;
  if (netLiq && netLiq > 0 && mktValueUsd !== null) {
    currentWeightPct = (mktValueUsd / netLiq) * 100;
  }

  return {
    conId: pos.conId,
    symbol: pos.description || `ConId: ${pos.conId}`,
    currency,
    shares,
    avgCost: Number(pos.avgCost) || 0,
    mktPrice: price,
    mktValue,
    realizedPnl: Number(pos.realizedPnl) || 0,
    unrealizedPnl,
    group: pos.group,
    sector: pos.sector,
    secType: pos.secType,
    priceUsd,
    mktValueUsd,
    unrealizedPnlUsd,
    currentWeightPct,
    targetWeightPct: null,
    targetQty: null,
    rawTargetQty: null,
    effectiveTargetQty: null,
    tradeQty: null,
    tradeValue: null,
    tradeValueUsd: null,
    fxRate: null,
    tradeAction: null,
    isSynthetic: false,
    isInTarget: false,
    timeZoneId: timeZoneMap?.[pos.conId] ?? null,
    minSize: minSize ?? null,
    userDiffId: null,
    isHeld: false,
    isExcluded: false,
    exclusionReasons: [],
    stageId: null,
    stageName: null,
    stageState: null,
    stageStartTime: null,
    stageEndTime: null,
    stageQty: null,
    hasDrift: false,
    orderId: null,
    orderStatus: null,
    filledQty: null,
    avgFillPrice: null,
    commission: null,
  };
}

export function isUsableStockQuote(
  quote: { price?: unknown } | undefined
): boolean {
  return isFinitePositiveNumber(quote?.price);
}

export function isUsableOptionCoverage(
  quote: OptionPriceResult | undefined,
  multiplier: unknown,
  shares: number
): boolean {
  if (!quote) return false;
  const selected = selectConservativeOptionPrice(quote, shares);
  return selected !== null && isFinitePositiveNumber(multiplier);
}
